/**
 * The busy state of a `.btn` on the static pages (#387), drawn exactly like
 * `UI.button` draws it in the app: the label stays in the button, invisible,
 * so the width holds; a spinner sits over it; the button is disabled and
 * `aria-busy`. The DOM half is `button-busy.ts`; what is here is pure, so the
 * markup can be tested against the Elm side's.
 */

/**
 * A spinner as Pencil draws it: `size` in px, `innerRadius` as the ratio of
 * the hole to the outer radius, and the opacity of the track under the arc.
 */
export interface SpinnerShape {
  size: number;
  innerRadius: number;
  trackOpacity: number;
}

/**
 * Pencil "NUMISTAR 2.0 — Coin Editor (Design)", state "Saving": a 20 px ring
 * with a 25 % track. Same values as `UI.buttonSpinner`.
 */
export const BUTTON_SPINNER: SpinnerShape = {
  size: 20,
  innerRadius: 0.78,
  trackOpacity: 0.25,
};

/** `UI.spinner`'s classes: under reduced motion the arc turns slower, not never. */
export const SPINNER_CLASS =
  "block shrink-0 animate-spin motion-reduce:[animation-duration:3s]";

/** Centres the spinner over the label. */
export const SPINNER_OVERLAY_CLASS =
  "absolute inset-0 flex items-center justify-center";

/**
 * Hides the label without taking it out of the layout or the accessibility
 * tree: `invisible` would also drop the button's accessible name.
 */
export const HIDDEN_LABEL_CLASS = "opacity-0";

/** Two decimals, as `UI.spinner` rounds, so 2.2 is not 2.1999999999999997. */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Ring thickness and radius of the circle the stroke is centred on. */
export function spinnerGeometry(shape: SpinnerShape): {
  thickness: number;
  radius: number;
  center: number;
} {
  const thickness = round2((shape.size / 2) * (1 - shape.innerRadius));
  return {
    thickness,
    radius: round2(shape.size / 2 - thickness / 2),
    center: shape.size / 2,
  };
}

/**
 * The spinner's SVG in `currentColor`: the track, and a 110° arc starting at
 * twelve o'clock (`pathLength` counts the dash in degrees). Built from
 * numbers only, never from page input, so it is safe as `innerHTML`.
 */
export function spinnerSvg(shape: SpinnerShape): string {
  const geometry = spinnerGeometry(shape);
  const thickness = String(geometry.thickness);
  const radius = String(geometry.radius);
  const center = String(geometry.center);
  const size = String(shape.size);
  const trackOpacity = String(shape.trackOpacity);
  const ring = `cx="${center}" cy="${center}" r="${radius}" fill="none" stroke="currentColor" stroke-width="${thickness}"`;
  return (
    `<svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" class="${SPINNER_CLASS}" aria-hidden="true">` +
    `<circle ${ring} stroke-opacity="${trackOpacity}"></circle>` +
    `<circle ${ring} pathLength="360" stroke-dasharray="110 250" transform="rotate(-90 ${center} ${center})"></circle>` +
    `</svg>`
  );
}
